import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Save, Sparkles, FileText, Shield, ScrollText, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

const LEGAL_FORMS = [
  'Auto-entrepreneur',
  'SARL',
  'SAS',
  'SASU',
  'EURL',
  'SA',
  'SCI',
  'Association loi 1901',
  'Autre'
];

const LEGAL_PAGES = [
  { key: 'mentions_legales', label: 'Mentions Legales', icon: FileText, description: 'Obligatoires pour tout site web' },
  { key: 'politique_confidentialite', label: 'Politique de Confidentialite', icon: Shield, description: 'Conforme au RGPD' },
  { key: 'cgv', label: 'Conditions Generales de Vente', icon: ScrollText, description: 'Pour la vente en ligne' },
];

const ShopLegal = () => {
  const { shopId } = useParams();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [expandedPage, setExpandedPage] = useState(null);

  const [legalInfo, setLegalInfo] = useState({
    companyName: '',
    legalForm: '',
    siret: '',
    tvaNumber: '',
    representativeName: '',
    contactEmail: '',
    contactPhone: '',
    headquartersAddress: '',
    hostingProvider: 'Emergent Labs',
    activityDescription: ''
  });

  const [legalPages, setLegalPages] = useState({
    mentions_legales: '',
    politique_confidentialite: '',
    cgv: ''
  });

  useEffect(() => {
    fetchLegalData();
  }, [shopId]);

  const fetchLegalData = async () => {
    try {
      const response = await api.get(`/shops/${shopId}/legal-info`);
      if (response.data.legalInfo) {
        setLegalInfo(prev => ({ ...prev, ...response.data.legalInfo }));
      }
      if (response.data.legalPages) {
        setLegalPages(prev => ({ ...prev, ...response.data.legalPages }));
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveInfo = async () => {
    setSaving(true);
    try {
      await api.put(`/shops/${shopId}/legal-info`, legalInfo);
      toast.success('Informations enregistrees !');
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async (pageType) => {
    if (!legalInfo.companyName) {
      toast.error('Veuillez d\'abord remplir le nom de votre societe');
      return;
    }
    setGenerating(pageType);
    try {
      const response = await api.post(`/shops/${shopId}/generate-legal`, { pageType });
      setLegalPages(prev => ({ ...prev, [pageType]: response.data.content }));
      setExpandedPage(pageType);
      toast.success('Document genere avec succes !');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de la generation';
      toast.error(msg);
    } finally {
      setGenerating(null);
    }
  };

  const handleSavePage = async (pageType) => {
    setSaving(true);
    try {
      await api.put(`/shops/${shopId}/legal-pages/${pageType}`, { content: legalPages[pageType] });
      toast.success('Document sauvegarde !');
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="shop-legal">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Mentions Legales</h1>
        <p className="text-muted-foreground mt-1">Informations de votre societe et pages legales</p>
      </div>

      {/* Company Info Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Informations de la societe
          </CardTitle>
          <CardDescription>Ces informations seront utilisees pour generer vos pages legales</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="companyName">Raison sociale *</Label>
              <Input
                id="companyName"
                placeholder="Ma Societe SAS"
                value={legalInfo.companyName}
                onChange={(e) => setLegalInfo({ ...legalInfo, companyName: e.target.value })}
                data-testid="legal-company-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="legalForm">Forme juridique</Label>
              <select
                id="legalForm"
                value={legalInfo.legalForm}
                onChange={(e) => setLegalInfo({ ...legalInfo, legalForm: e.target.value })}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid="legal-form-select"
              >
                <option value="">Selectionnez...</option>
                {LEGAL_FORMS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="siret">SIRET</Label>
              <Input
                id="siret"
                placeholder="123 456 789 00012"
                value={legalInfo.siret}
                onChange={(e) => setLegalInfo({ ...legalInfo, siret: e.target.value })}
                data-testid="legal-siret"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tvaNumber">Numero de TVA</Label>
              <Input
                id="tvaNumber"
                placeholder="FR12345678901"
                value={legalInfo.tvaNumber}
                onChange={(e) => setLegalInfo({ ...legalInfo, tvaNumber: e.target.value })}
                data-testid="legal-tva"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="representativeName">Representant legal</Label>
            <Input
              id="representativeName"
              placeholder="Jean Dupont"
              value={legalInfo.representativeName}
              onChange={(e) => setLegalInfo({ ...legalInfo, representativeName: e.target.value })}
              data-testid="legal-representative"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contactEmail">Email de contact</Label>
              <Input
                id="contactEmail"
                type="email"
                placeholder="contact@masociete.fr"
                value={legalInfo.contactEmail}
                onChange={(e) => setLegalInfo({ ...legalInfo, contactEmail: e.target.value })}
                data-testid="legal-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contactPhone">Telephone</Label>
              <Input
                id="contactPhone"
                type="tel"
                placeholder="+33 1 23 45 67 89"
                value={legalInfo.contactPhone}
                onChange={(e) => setLegalInfo({ ...legalInfo, contactPhone: e.target.value })}
                data-testid="legal-phone"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="headquartersAddress">Adresse du siege social</Label>
            <Textarea
              id="headquartersAddress"
              placeholder="123 Rue du Commerce, 75001 Paris"
              value={legalInfo.headquartersAddress}
              onChange={(e) => setLegalInfo({ ...legalInfo, headquartersAddress: e.target.value })}
              data-testid="legal-address"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="activityDescription">Description de l'activite</Label>
            <Textarea
              id="activityDescription"
              placeholder="Vente en ligne de vetements et accessoires de mode..."
              value={legalInfo.activityDescription}
              onChange={(e) => setLegalInfo({ ...legalInfo, activityDescription: e.target.value })}
              data-testid="legal-activity"
            />
          </div>
          <div className="flex justify-end">
            <Button onClick={handleSaveInfo} disabled={saving} data-testid="save-legal-info">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer les informations'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Legal Pages Generation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            Pages legales
          </CardTitle>
          <CardDescription>Generez vos documents legaux grace a l'IA, puis editez-les si besoin</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {LEGAL_PAGES.map(({ key, label, icon: Icon, description }) => {
            const isExpanded = expandedPage === key;
            const hasContent = !!legalPages[key];
            const isGenerating = generating === key;
            return (
              <div key={key} className="border rounded-xl overflow-hidden" data-testid={`legal-page-${key}`}>
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setExpandedPage(isExpanded ? null : key)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${hasContent ? 'bg-green-500/10 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{label}</p>
                      <p className="text-xs text-muted-foreground">{hasContent ? 'Document genere' : description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant={hasContent ? "outline" : "default"}
                      onClick={(e) => { e.stopPropagation(); handleGenerate(key); }}
                      disabled={isGenerating || generating !== null}
                      data-testid={`generate-${key}`}
                    >
                      {isGenerating ? (
                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generation...</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-1" /> {hasContent ? 'Regenerer' : 'Generer'}</>
                      )}
                    </Button>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3 border-t">
                    <Textarea
                      value={legalPages[key] || ''}
                      onChange={(e) => setLegalPages(prev => ({ ...prev, [key]: e.target.value }))}
                      placeholder="Le contenu apparaitra ici apres la generation..."
                      className="min-h-[300px] mt-3 font-mono text-xs"
                      data-testid={`legal-content-${key}`}
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={() => handleSavePage(key)}
                        disabled={saving || !legalPages[key]}
                        data-testid={`save-${key}`}
                      >
                        <Save className="w-4 h-4 mr-1" /> Sauvegarder
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShopLegal;
